// Tesseract reads best with ~30px+ tall glyphs, dark text on a light background
// and some margin around the text. Screenshots from small screens (or crops
// of a zoomed-out chart) give tiny anti-aliased text, so we upscale, convert
// to grayscale, invert dark themes, stretch contrast and pad the image.
const TARGET_WIDTH = 600
const MAX_SCALE = 2.5
const PADDING = 24

export const prepareForOcr = (source: HTMLCanvasElement): HTMLCanvasElement => {
  const scale = Math.min(MAX_SCALE, Math.max(1, TARGET_WIDTH / source.width))
  const width = Math.round(source.width * scale)
  const height = Math.round(source.height * scale)

  const canvas = document.createElement('canvas')
  canvas.width = width + PADDING * 2
  canvas.height = height + PADDING * 2

  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) {
    return source
  }

  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(source, PADDING, PADDING, width, height)

  const imageData = ctx.getImageData(PADDING, PADDING, width, height)
  const { data } = imageData
  const gray = new Uint8ClampedArray(width * height)

  let sum = 0
  let min = 255
  let max = 0
  for (let i = 0; i < gray.length; i++) {
    const value = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2]
    gray[i] = value
    sum += value
    if (value < min) min = value
    if (value > max) max = value
  }

  // Mostly dark pixels means a dark theme chart: invert to get dark text on light background
  const invert = sum / gray.length < 128
  const range = Math.max(1, max - min)

  for (let i = 0; i < gray.length; i++) {
    let value = ((gray[i] - min) / range) * 255
    if (invert) value = 255 - value
    data[i * 4] = value
    data[i * 4 + 1] = value
    data[i * 4 + 2] = value
    data[i * 4 + 3] = 255
  }

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.putImageData(imageData, PADDING, PADDING)

  return canvas
}
